import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import { Plus } from 'lucide-react';
import ScopeItem from './ScopeItem';
import axiosInstance from '../../../utils/axios';
import { generateOutput } from './helper';
import { isReadOnly } from '../../../utils/permissionUtils';

const ScopeManager = ({ data, scopeList = [], currentLaydown, loader, setDropdownVaue, laydownList }) => {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    pendingAction: null,
    pendingParams: null,
    currentEditingScopeId: null
  });

  const [btnLoader, setBtnLoader] = useState(false)

  const [scopeListHash, setScopeListHash] = useState('');

  useEffect(() => {
    const newHash = JSON.stringify(scopeList?.map(s => ({ 
      scope: s?.scope, 
      tracker_row_count: s?.tracker_row_count, 
      bom_count: s?.bom_count 
    })));
      setScopeListHash(newHash);
  }, [scopeList, scopeListHash]);

  useEffect(() => {
    if (scopeList.length) {
      
        const initialScopes = scopeList.map((scopeId, index) => ({
          id: scopeId?.scope,
          scopeId: scopeId?.scope,
          blockSelection: {},
          parts: [],
          editing: false,
          selectedItems: new Set(),
          isPartsEnabled: false,
          isLoadingParts: false,
          isLoadingButton: false,
          isLoadingDelete: false,
          partList: [],
          isLoadingDropdownData: false,
          isPopoverEnabled: false,
          existingPartsDisplay: [],
          isNewRow: false,
          partCount: scopeId?.tracker_row_count,
          trackerCount: scopeId?.bom_count,
          manuallyUncheckedParts: new Set() 
        }));
        setScopes(initialScopes);
    } else if (scopes.length > 0) {
      setScopes([]);
    }
    // eslint-disable-next-line
  }, [scopeListHash, scopeList]); 

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const getNextScopeId = () => {
    const existingIds = scopes.map(scope => scope.scopeId);
    let nextId = 1;
    while (existingIds.includes(nextId)) {
      nextId++;
    }
    return nextId;
  };

  const getCurrentEditingScope = () => {
    return scopes.find(scope => scope.editing);
  };

  const handleConfirmSave = async () => {
    const currentEditingScope = getCurrentEditingScope();
    if (currentEditingScope) {
      await handleSave(currentEditingScope.id);
    }
    
    if (confirmDialog.pendingAction && confirmDialog.pendingParams) {
      confirmDialog.pendingAction(...confirmDialog.pendingParams);
    }
    
    setConfirmDialog({
      open: false,
      pendingAction: null,
      pendingParams: null,
      currentEditingScopeId: null
    });
  };

  const handleConfirmCancel = () => {
    setConfirmDialog({
      open: false,
      pendingAction: null,
      pendingParams: null,
      currentEditingScopeId: null
    });
  };

  const checkForEditingScope = (action, params) => {
    const currentEditingScope = getCurrentEditingScope();
    
    if (currentEditingScope) {
      setConfirmDialog({
        open: true,
        pendingAction: action,
        pendingParams: params,
        currentEditingScopeId: currentEditingScope.id
      });
      return true;
    }
    return false;
  };

  const handleAddScope = async () => {
    if (checkForEditingScope(handleAddScope, [])) {
      return;
    }

    setLoading(true);
    try {
      const newScopeId = getNextScopeId();
      await setDropdownVaue(currentLaydown?.id);
      setScopes(prev => [
        ...prev,
        {
          id: newScopeId,
          scopeId: newScopeId,
          blockSelection: {},
          parts: [],
          editing: true,
          selectedItems: new Set(),
          isPartsEnabled: false,
          isLoadingParts: false,
          isLoadingButton: false,
          isLoadingDelete: false,
          partList: [],
          isLoadingDropdownData: false,
          isPopoverEnabled: true,
          existingPartsDisplay: [],
          isNewRow: true,
          partCount: null,
          trackerCount: null,
          manuallyUncheckedParts: new Set()
        }
      ]);
    } catch (err) {
      console.error(err);
      showSnackbar('Failed to add scope', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateScope = (scopeId, updates) => {
    setScopes(prev =>
      prev.map(scope =>
        scope.id === scopeId ? { ...scope, ...updates } : scope
      )
    );
  };

  const removeScope = (scopeId) => {
    setScopes(prev => prev.filter(scope => scope.id !== scopeId));
  };

  const fetchDropdownData = async (scopeId) => {
    try {
      updateScope(scopeId, { isLoadingDropdownData: true });

      const res = await axiosInstance.post(`/laydown-scope/v2/unassigned-bom-detailed?laydown_id=${currentLaydown?.id}&scope=${scopeId}`);

        const processedSelectedItems = new Set();
        const blockSelection = {};

        const initialSelectedParts = [];
        if (res.data && Array.isArray(res.data)) {
          res.data.forEach(part => {
            if (part.scope_assigned && !part.fully_assigned) {
              initialSelectedParts.push(part.bom_id);
            }
          });
        }

        await setDropdownVaue(currentLaydown?.id, scopeId);

        updateScope(scopeId, {
          isLoadingDropdownData: false,
          selectedItems: processedSelectedItems,
          blockSelection: blockSelection,
          isPopoverEnabled: true,
          isPartsEnabled: true,
          partList: res.data || [],
          parts: initialSelectedParts,
          existingPartsDisplay: initialSelectedParts,
          manuallyUncheckedParts: new Set() 
        });
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
      updateScope(scopeId, { isLoadingDropdownData: false });
      showSnackbar('Failed to fetch data', 'error');
    }
  };

  const getAutoIncludedParts = (partList, manuallyUncheckedParts = new Set()) => {
    return partList
      .filter(part => part.scope_assigned === true && !manuallyUncheckedParts.has(part.bom_id))
      .map(part => part.bom_id);
  };

const handleSave = async (scopeId) => {
  const scope = scopes.find((item) => item.id === scopeId);
  if (!scope) return;

  if (scope.selectedItems.size === 0 || scope.parts.length === 0) {
    showSnackbar('Please select valid block and parts in dropdown', 'error');
    return;
  }

  setBtnLoader(true)
  updateScope(scopeId, { isLoadingButton: true });

  const autoIncludedParts = getAutoIncludedParts(scope.partList || [], scope.manuallyUncheckedParts);
  const finalParts = [...new Set([...scope.parts, ...autoIncludedParts])];

  const generatedOutput = generateOutput(data, scope.selectedItems);

  const isBlockSelectionEmpty =
    !scope.blockSelection || Object.keys(scope.blockSelection).length === 0;

  const scopeSelectionJson = isBlockSelectionEmpty
    ? generatedOutput
    : scope.blockSelection;

  try {
    const res = await axiosInstance.post(
      `/laydown-scope/v2/scope?laydown_id=${currentLaydown?.id}&scope=${scope.scopeId}`,
      {
        scope_selection_json: scopeSelectionJson,
        bom_ids: finalParts
      }
    );

    if (res.data) {
      showSnackbar('Scope saved successfully', 'success');
    
      updateScope(scopeId, {
        editing: false,
        isLoadingButton: false,
        isPopoverEnabled: false,
        isPartsEnabled: false,
        existingPartsDisplay: finalParts,
        isNewRow: false,
        parts: finalParts,
        manuallyUncheckedParts: new Set(),
        partList: [], 
        blockSelection: {}, 
        selectedItems: new Set() 
      });
    }
  } catch (err) {
    console.error('Save failed:', err);
    showSnackbar('Failed to save scope. Please try again', 'error');
    updateScope(scopeId, {
      isLoadingButton: false
    });
  } finally {
    setBtnLoader(false)
  }
};
  const handleCancel = (scopeId) => {
    const scope = scopes.find(s => s.id === scopeId);

    if (scope && scope.isNewRow && !scopeList.find(s => s.scope === scope.scopeId)) {
      removeScope(scopeId);
    } else {
      updateScope(scopeId, {
        editing: false,
        isPopoverEnabled: false,
        isPartsEnabled: false,
        blockSelection: {},
        parts: scope.existingPartsDisplay || [],
        selectedItems: new Set(),
        manuallyUncheckedParts: new Set() 
      });
    }
  };

  const handleEdit = async (scopeId) => {
    if (checkForEditingScope(handleEdit, [scopeId])) {
      return;
    }

    updateScope(scopeId, {
      editing: true,
      isPopoverEnabled: true,
      isPartsEnabled: true,
      isNewRow: false
    });
    await fetchDropdownData(scopeId);
  };

  const handleDelete = async (scopeId) => {
    if (checkForEditingScope(handleDelete, [scopeId])) {
      return;
    }

    const scope = scopes.find(s => s.id === scopeId);
    if (!scope) return;

    updateScope(scopeId, { isLoadingDelete: true });

    try {
      const response = await axiosInstance.delete(`/laydown-scope/v2/scope?laydown_id=${currentLaydown?.id}&scope=${scope.scopeId}`);

      if (response.status === 200 || response.status === 204) {
        removeScope(scopeId);
        showSnackbar('Scope deleted successfully', 'success');
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      console.error('Failed to delete scope:', error);
      updateScope(scopeId, { isLoadingDelete: false });
      showSnackbar('Failed to delete scope. Please try again', 'error');
    }
  };

  const makeApiCall = async (scopeId, blockSelection) => {
    try {
      updateScope(scopeId, { isLoadingParts: true });

      const response = await axiosInstance.post(`/laydown-scope/v2/unassigned-bom-detailed?laydown_id=${currentLaydown?.id}`, {
        "scope_selection_json": { ...blockSelection }
      });

      if (!response.data) {
        throw new Error('API call failed');
      }

      const result = response.data;
      let partList = [];

      if (result.data && Array.isArray(result.data)) {
        partList = result.data;
      } else if (Array.isArray(result)) {
        partList = result;
      } else {
        console.warn('No valid partList found in API response:', result);
        partList = [];
      }

      const initialSelectedParts = [];
      partList.forEach(part => {
        if (part.scope_assigned && !part.fully_assigned) {
          initialSelectedParts.push(part.bom_id);
        }
      });

      const currentScope = scopes.find(s => s.id === scopeId);
      const autoIncludedParts = getAutoIncludedParts(partList, currentScope.manuallyUncheckedParts);
      const finalParts = [...new Set([...currentScope.parts, ...initialSelectedParts, ...autoIncludedParts])];

      updateScope(scopeId, {
        isPartsEnabled: true,
        isLoadingParts: false,
        partList: partList,
        parts: finalParts
      });

      return result;
    } catch (error) {
      console.error('API call failed:', error);
      updateScope(scopeId, { isLoadingParts: false });
      throw error;
    }
  };

  const handleBlockSelection = async (scopeId, output) => {    
    updateScope(scopeId, { blockSelection: output });

    try {
      await makeApiCall(scopeId, output);
    } catch (error) {
      console.error('Failed to process block selection:', error);
    }
  };

  const handlePartChange = (scopeId, selectedBomIds) => {
    const currentScope = scopes.find(s => s.id === scopeId);
    if (!currentScope) return;

    const currentManuallyUnchecked = new Set(currentScope.manuallyUncheckedParts);
    
    if (currentScope.partList) {
      currentScope.partList.forEach(part => {
        if (part.scope_assigned === true) {
          if (!selectedBomIds.includes(part.bom_id)) {
            currentManuallyUnchecked.add(part.bom_id);
          } else {
            currentManuallyUnchecked.delete(part.bom_id);
          }
        }
      });

      const autoIncludedParts = getAutoIncludedParts(currentScope.partList, currentManuallyUnchecked);
      const finalParts = [...new Set([...selectedBomIds, ...autoIncludedParts])];
      
      updateScope(scopeId, { 
        parts: finalParts,
        manuallyUncheckedParts: currentManuallyUnchecked
      });
    } else {
      updateScope(scopeId, { parts: selectedBomIds });
    }
  };

  const handleSelectedItemsChange = (scopeId, newSelectedItems) => {
    updateScope(scopeId, { selectedItems: newSelectedItems });
  };

  if (loader) {
    return (
      <Box sx={{ width: "100%", height: "calc(100vh - 240px)", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pr: 4 }}>
      <Box sx={{ width: "100%", height: "calc(100vh - 260px)", overflow: "auto" }}>
        {scopes.map((scope, index) => (
          <ScopeItem
            key={scope.id}
            scope={scope}
            index={index}
            data={data}
            currentLaydown={currentLaydown}
            onSave={handleSave}
            onCancel={handleCancel}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBlockSelection={handleBlockSelection}
            onPartChange={handlePartChange}
            onSelectedItemsChange={handleSelectedItemsChange}
            updateScope={updateScope}
          />
        ))}
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={18} /> : <Plus size={18} />}
          onClick={handleAddScope}
          disabled={loading || isReadOnly() || !laydownList.length}
          sx={{
            borderStyle: 'dashed',
            color: 'red',
            borderColor: 'red',
            '&:hover': { borderColor: 'darkred' }
          }}
        >
          Add more scope
        </Button>
      </Box>

      <Dialog
        open={confirmDialog.open}
        onClose={handleConfirmCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes in the current scope. Would you like to save them before proceeding?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} loading={btnLoader} color="primary" variant="contained">
            Save & Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScopeManager;